/**
 * Web Audio chime engine — synthesised dual-tone bell, zero external assets.
 * Instantiate `chimeEngine.playHandoffChime()` on user gesture path so mobile
 * autoplay policy is respected. Volume 0-1.
 */
class NotificationAudioEngine {
  constructor() {
    this.ctx = null;
  }

  init() {
    if (typeof window === "undefined") return;
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
  }

  async ensureActive() {
    this.init();
    if (!this.ctx) return false;
    if (this.ctx.state === "suspended") {
      try {
        await this.ctx.resume();
      } catch {
        return false;
      }
    }
    return true;
  }

  playHandoffChime(volume = 0.35) {
    this.init();
    if (!this.ctx) return;
    if (this.ctx.state === "suspended") this.ctx.resume().catch(() => {});
    const now = this.ctx.currentTime;

    const master = this.ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(Math.min(0.9, volume), now + 0.04);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
    master.connect(this.ctx.destination);

    // Layer 1: sine E5 → A5 slide (harmonic bell)
    const osc1 = this.ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(659.25, now);
    osc1.frequency.exponentialRampToValueAtTime(880.0, now + 0.15);
    osc1.connect(master);
    osc1.start(now);
    osc1.stop(now + 1.2);

    // Layer 2: triangle B5, arpeggiated 80ms after (softer)
    const osc2 = this.ctx.createOscillator();
    osc2.type = "triangle";
    osc2.frequency.setValueAtTime(987.77, now + 0.08);
    const g2 = this.ctx.createGain();
    g2.gain.setValueAtTime(0.55, now + 0.08);
    osc2.connect(g2);
    g2.connect(master);
    osc2.start(now + 0.08);
    osc2.stop(now + 1.2);
  }

  playTick(volume = 0.12) {
    this.init();
    if (!this.ctx) return;
    if (this.ctx.state === "suspended") return;
    const now = this.ctx.currentTime;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
    gain.connect(this.ctx.destination);
    const osc = this.ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1320, now);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.16);
  }
}

export const chimeEngine = new NotificationAudioEngine();
